import { useEffect, useRef, useState } from 'react';
// import { format, isValid, parse } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import { useDispatch, useSelector } from 'react-redux';
import { getAllReservations } from '../redux/slices/reservationSlice';
import { useTranslation } from 'react-i18next';

import './calendar-picker.css';
const CalendarDialog = ({ value, onChange, equipmentId }) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const dispatch = useDispatch();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState(value);

  const reservations = useSelector((state) => state.reservations.reservations);

  useEffect(() => {
    if (!value || !value.from) {
      setSelectedRange(undefined);
    } else {
      setSelectedRange(value);
    }
  }, [value]);

  useEffect(() => {
    if (dialogRef.current) {
      if (isDialogOpen) {
        dialogRef.current.showModal();
      } else {
        dialogRef.current.close();
      }
    }
  }, [isDialogOpen]);

  //get reservations for each type of equipment
  useEffect(() => {
    if (typeof equipmentId === 'string' && equipmentId.trim() !== '') {
      dispatch(getAllReservations(equipmentId));
    }
  }, [equipmentId, dispatch]);

  const toggleDialog = () => {
    setIsDialogOpen((prev) => !prev);
  };

  const handleDayPickerSelect = (range) => {
    // If no range is selected, do nothing
    //if (!range) return;

    // If user clicks the same start date again, clear the selection
    if (
      selectedRange?.from &&
      !selectedRange.to &&
      range.from &&
      selectedRange.from.getTime() === range.from.getTime()
    ) {
      setSelectedRange(undefined);
      onChange(undefined);
      return;
    }

    // Otherwise, update the selection normally
    setSelectedRange(range);
    onChange(range);
  };

  const handleDialogClick = (e) => {
    e.stopPropagation();
  };

  //pull reserved dates
  const getReservedDates = () => {
    const reserved = [];

    reservations.forEach((res) => {
      if (
        !res ||
        !res.startDate ||
        !res.endDate ||
        res.reservationStatus?.toLowerCase() === 'canceled'
      ) {
        return;
      }

      const start = new Date(res.startDate);
      const end = new Date(res.endDate);

      if (isNaN(start) || isNaN(end)) return;

      let d = new Date(start);
      while (d <= end) {
        reserved.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }
    });
    return reserved;
  };

  //sets disabled dates to before current date and any reserved dates
  const getDisabledDates = () => {
    const today = new Date();
    const disabled = [{ before: today }];
    const reserved = getReservedDates();

    if (
      !selectedRange ||
      !selectedRange.from ||
      selectedRange.from === null ||
      isNaN(new Date(selectedRange.from).getTime())
    ) {
      return [...disabled, ...reserved];
    }

    const startDate = new Date(selectedRange.from);

    // Find the next conflicting reservation
    const nextConflict = reservations
      .filter((res) => {
        if (
          !res ||
          !res.startDate ||
          !res.endDate ||
          res.reservationStatus?.toLowerCase() === 'canceled'
        ) {
          return false;
        }
        return new Date(res.startDate) > startDate;
      })
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))[0];

    if (nextConflict) {
      const cutoff = new Date(nextConflict.endDate);
      // Disable all dates after the conflict
      return [...disabled, ...reserved, { after: cutoff }];
    }
    return [...disabled, ...reserved];
  };

  return (
    <div>
      <button
        className="border-2 border-lime-700 text-lime-700 px-4 py-1 rounded-full hover:bg-lime-50 text-md"
        onClick={toggleDialog}
      >
        {t('farmshareInProgress.calendarDialog.selectDateRange')}
      </button>
      <dialog ref={dialogRef} onClick={toggleDialog} className="border-2 rounded-lg">
        <div className="p-4 flex justify-center items-center flex-col" onClick={handleDialogClick}>
          <DayPicker
            mode="range"
            numberOfMonths={2}
            selected={selectedRange}
            onSelect={handleDayPickerSelect}
            disabled={getDisabledDates()} //includes reserved dates
          />
          <button
            className="mb-4 p-2 bg-green-700 text-white rounded hover:bg-green-800"
            onClick={() => setIsDialogOpen(false)}
          >
            Confirm Dates
          </button>
        </div>
      </dialog>
    </div>
  );
};

export { CalendarDialog };
